<script lang="ts">
	import { Button } from '$lib/components/ui/button';
	import { Card, CardContent } from '$lib/components/ui/card';
	import { Badge } from '$lib/components/ui/badge';
	import { createPageSEO, createFAQSchema } from '$lib/config/seo';
	import SvelteSeo from 'svelte-seo';
	import GlopayCtaHorizontal from '$lib/components/misc/glopay-cta-inline/glopay-cta-inline.svelte';

	const faqs = [
		{
			question: 'How do you verify production experience?',
			answer:
				"We analyze GitHub repos using AI trained on data engineering patterns. We look for proper error handling, data quality checks, performance optimization, monitoring—code that shows operational understanding. Tutorial projects don't pass."
		},
		{
			question: 'What if I need a very specific stack?',
			answer:
				"That's our specialty. Tell us exactly what you need: \"medallion architecture on Databricks with dbt, optimize Kafka streaming.\" Our AI understands technical specificity. If we don't have exact matches, we'll suggest adjacent tech and be upfront about it."
		},
		{
			question: 'How quickly can I start interviewing?',
			answer:
				"Most clients get 3-5 matches within 48 hours. Engineers respond within 24 hours. Average time from JD submission to first hire: 12 days. We've seen companies hire in 5 days when they find the right fit fast."
		},
		{
			question: "What's your pricing?",
			answer:
				"First 5 matches: free. After that: 15% of first-year salary for full-time, 10% for contractors. No subscription, no per-match fees. If your hire doesn't work out in 90 days, we find a replacement free."
		},
		{
			question: 'Can you find contractors?',
			answer:
				'Yes. ~40% of engineers on our platform do contract work. Same vetting, same quality bar. Perfect for 3-month migrations or filling gaps while you hire full-time.'
		}
	];

	// SEO Configuration - now using the faqs variable
	const seoConfig = createPageSEO({
		title: 'Hire Data Engineers | Pre-Vetted Talent | TalentProfile',
		description:
			'Hire expert data engineers skilled in Python, SQL, Spark, and cloud platforms. Submit your job description, get matched with pre-vetted engineers in 48 hours. Average time to hire: 12 days.',
		canonical: '/hire-data-engineers',
		keywords:
			'hire data engineers, data engineer jobs, Python developers, SQL experts, Spark engineers, ETL developers, data pipeline engineers',
		ogImage: '/og-data-engineers.jpg',
		structuredData: createFAQSchema(faqs)
	});
</script>

<SvelteSeo {...seoConfig} />

<div class="flex min-h-[calc(100dvh-527px)] flex-col">
	<!-- Hero - Clean & Professional -->
	<section class="bg-white py-20">
		<div class="container mx-auto px-4">
			<div class="mx-auto max-w-5xl">
				<div class="mb-6 flex items-center justify-center gap-2">
					<div
						class="rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-sm font-medium text-blue-700"
					>
						✓ 2,847 Pre-Vetted Engineers
					</div>
					<div
						class="rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-sm font-medium text-blue-700"
					>
						✓ 48-Hour Matching
					</div>
				</div>

				<h1 class="mb-6 text-center text-5xl leading-tight font-bold text-gray-900 md:text-6xl">
					Hire <span class="text-blue-600">Data Engineers</span> Who Knows What They're Doing
				</h1>

				<p
					class="mx-auto mb-10 max-w-3xl text-center text-xl leading-relaxed text-gray-600 md:text-2xl"
				>
					Get matched with data engineers who've actually shipped production pipelines.
					<span class="font-semibold text-gray-900"
						>Submit your JD → We analyze & match → Interview in 48 hours.</span
					>
				</p>

				<div class="flex flex-col items-center justify-center gap-4 sm:flex-row">
					<Button
						size="lg"
						class="bg-blue-600 px-10 py-6 text-lg font-semibold text-white hover:bg-blue-700"
					>
						📄 Submit Job Description
					</Button>
					<Button
						size="lg"
						variant="outline"
						class="border-2 border-gray-300 px-10 py-6 text-lg font-semibold text-gray-900 hover:bg-gray-50"
					>
						See How It Works
					</Button>
				</div>

				<div class="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
					<div class="flex items-center gap-2">
						<svg class="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
							<path
								fill-rule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
								clip-rule="evenodd"
							/>
						</svg>
						<span>Free for first 5 matches</span>
					</div>
					<div class="flex items-center gap-2">
						<svg class="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
							<path
								fill-rule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
								clip-rule="evenodd"
							/>
						</svg>
						<span>No registration required</span>
					</div>
					<div class="flex items-center gap-2">
						<svg class="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
							<path
								fill-rule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
								clip-rule="evenodd"
							/>
						</svg>
						<span>Pay only when you hire</span>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Stats Bar - Clean -->
	<section class="border-y border-gray-200 bg-gray-50 py-8">
		<div class="container mx-auto px-4">
			<div class="flex flex-wrap justify-center gap-8 md:gap-16">
				<div class="text-center">
					<div class="text-3xl font-bold text-blue-600 md:text-4xl">12 days</div>
					<div class="text-sm text-gray-600">Avg Time to Hire</div>
				</div>
				<div class="border-r border-l border-gray-300 px-8 text-center">
					<div class="text-3xl font-bold text-blue-600 md:text-4xl">94%</div>
					<div class="text-sm text-gray-600">Production Ready</div>
				</div>
				<div class="text-center">
					<div class="text-3xl font-bold text-blue-600 md:text-4xl">5.2TB+</div>
					<div class="text-sm text-gray-600">Data Processed Daily</div>
				</div>
			</div>
		</div>
	</section>

	<!-- The Problem - Clean Contrast -->
	<section class="bg-white py-20">
		<div class="container mx-auto px-4">
			<div class="mx-auto max-w-5xl">
				<div class="mb-12 text-center">
					<h2 class="mb-4 text-4xl font-bold text-gray-900 md:text-5xl">
						The Traditional Hiring Process is <span class="text-red-600">Broken</span>
					</h2>
					<p class="mx-auto max-w-2xl text-xl text-gray-600">
						You've been there: 200 applications, 5% relevant, weeks wasted on phone screens
					</p>
				</div>

				<div class="mb-12 grid gap-8 md:grid-cols-2">
					<!-- Bad Way -->
					<Card class="border-2 border-red-200 bg-red-50">
						<CardContent class="p-8">
							<div class="mb-6 flex items-center gap-3">
								<div class="flex h-12 w-12 items-center justify-center rounded-xl bg-red-500">
									<svg
										class="h-6 w-6 text-white"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M6 18L18 6M6 6l12 12"
										/>
									</svg>
								</div>
								<h3 class="text-2xl font-bold text-gray-900">Traditional Job Boards</h3>
							</div>
							<div class="space-y-4">
								<div class="flex items-start gap-3">
									<span class="text-lg font-bold text-red-500">❌</span>
									<p class="text-gray-700">"5 years of Spark" but never optimized a join</p>
								</div>
								<div class="flex items-start gap-3">
									<span class="text-lg font-bold text-red-500">❌</span>
									<p class="text-gray-700">"SQL Expert" can't explain a window function</p>
								</div>
								<div class="flex items-start gap-3">
									<span class="text-lg font-bold text-red-500">❌</span>
									<p class="text-gray-700">200+ applications, maybe 10 worth reviewing</p>
								</div>
								<div class="flex items-start gap-3">
									<span class="text-lg font-bold text-red-500">❌</span>
									<p class="text-gray-700">3 weeks of screening → zero quality candidates</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<!-- Good Way -->
					<Card class="border-2 border-green-200 bg-green-50">
						<CardContent class="p-8">
							<div class="mb-6 flex items-center gap-3">
								<div class="superellipse flex h-12 w-12 items-center justify-center bg-green-500">
									<svg
										class="h-6 w-6 text-white"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M5 13l4 4L19 7"
										/>
									</svg>
								</div>
								<h3 class="text-2xl font-bold text-gray-900">TalentProfile</h3>
							</div>
							<div class="space-y-4">
								<div class="flex items-start gap-3">
									<span class="text-lg font-bold text-green-500">✓</span>
									<p class="text-gray-700">Verified GitHub repos with real data pipelines</p>
								</div>
								<div class="flex items-start gap-3">
									<span class="text-lg font-bold text-green-500">✓</span>
									<p class="text-gray-700">AI validates production experience at scale</p>
								</div>
								<div class="flex items-start gap-3">
									<span class="text-lg font-bold text-green-500">✓</span>
									<p class="text-gray-700">3-5 pre-qualified matches, all worth talking to</p>
								</div>
								<div class="flex items-start gap-3">
									<span class="text-lg font-bold text-green-500">✓</span>
									<p class="text-gray-700">First interview scheduled in 48 hours</p>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>

				<!-- Story Box -->
				<div class="rounded-lg border border-blue-200 bg-blue-50 p-8">
					<div class="flex items-start gap-4">
						<div
							class="superellipse flex h-12 w-12 flex-shrink-0 items-center justify-center bg-blue-600 text-xl font-bold text-white"
						>
							💡
						</div>
						<div>
							<h3 class="mb-3 text-xl font-bold text-gray-900">Why We Built This</h3>
							<p class="mb-3 leading-relaxed text-gray-700">
								Our founder spent six months trying to hire a senior data engineer. After 300+
								applications and 20 technical interviews, they found exactly one person who could
								architect a modern data stack.
							</p>
							<p class="leading-relaxed text-gray-700">
								The problem wasn't the talent—it was the matching. Resumes lie. Job boards don't
								verify skills. Engineers who can actually build production systems get buried in
								noise. So we built TalentProfile to solve it: <strong
									>AI that analyzes real code to find engineers who've done the work.</strong
								>
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- What We Verify - Colorful Cards -->
	<section class="flex flex-col bg-white py-20">
		<div class="container mx-auto px-4">
			<div class="mx-auto max-w-6xl">
				<div class="mb-16 text-center">
					<h2 class="mb-4 text-4xl font-bold md:text-5xl">What "Verified" Actually Means</h2>
					<p class="mx-auto max-w-3xl text-xl text-gray-600">
						Anyone can claim they know Spark. We analyze their GitHub to prove it.
					</p>
				</div>

				<div class="grid gap-8 md:grid-cols-3">
					<!-- Card 1 - Blue -->
					<div class="group">
						<div
							class="rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 p-1 transition-all duration-300 hover:shadow-2xl"
						>
							<div class="h-full rounded-xl bg-white p-6">
								<div
									class="superellipse mb-4 flex h-14 w-14 items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600 transition-transform group-hover:scale-110"
								>
									<svg
										class="h-7 w-7 text-white"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
										/>
									</svg>
								</div>
								<h3 class="mb-3 text-xl font-bold">Code Quality</h3>
								<p class="mb-4 text-sm leading-relaxed text-gray-700">
									We scan for modular SQL, well-structured DAGs, proper error handling, and edge
									cases. Engineers with hardcoded credentials or 2,000-line files don't make it.
								</p>
								<div class="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
									<strong>Red flags:</strong> No tests, no docs, tutorial-only projects
								</div>
							</div>
						</div>
					</div>

					<!-- Card 2 - Purple -->
					<div class="group">
						<div
							class="rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 p-1 transition-all duration-300 hover:shadow-2xl"
						>
							<div class="h-full rounded-xl bg-white p-6">
								<div
									class="superellipse mb-4 flex h-14 w-14 items-center justify-center bg-gradient-to-br from-purple-500 to-purple-600 transition-transform group-hover:scale-110"
								>
									<svg
										class="h-7 w-7 text-white"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
										/>
									</svg>
								</div>
								<h3 class="mb-3 text-xl font-bold">Production Scale</h3>
								<p class="mb-4 text-sm leading-relaxed text-gray-700">
									We differentiate "used Spark" from "designed Spark jobs processing millions of
									records daily." We look for performance optimization, late-arriving data handling,
									partition management.
								</p>
								<div class="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
									<strong>We validate:</strong> Data volume, optimization, monitoring
								</div>
							</div>
						</div>
					</div>

					<!-- Card 3 - Indigo -->
					<div class="group">
						<div
							class="rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-600 p-1 transition-all duration-300 hover:shadow-2xl"
						>
							<div class="h-full rounded-xl bg-white p-6">
								<div
									class="superellipse mb-4 flex h-14 w-14 items-center justify-center bg-gradient-to-br from-indigo-500 to-indigo-600 transition-transform group-hover:scale-110"
								>
									<svg
										class="h-7 w-7 text-white"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
										/>
									</svg>
								</div>
								<h3 class="mb-3 text-xl font-bold">System Architecture</h3>
								<p class="mb-4 text-sm leading-relaxed text-gray-700">
									Good engineers think holistically. We verify they understand data modeling,
									pipeline orchestration, idempotency, lineage, and cost optimization.
								</p>
								<div class="rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
									<strong>Signals:</strong> Dimensional modeling, incremental processing
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="mx-auto mt-18 max-w-4xl">
			<GlopayCtaHorizontal placement="hire-data-engineers-post-what-verified-means" />
		</div>
	</section>

	<!-- Tech Stack - Clean Grid -->
	<section class="bg-white py-20">
		<div class="container mx-auto px-4">
			<div class="mx-auto max-w-5xl">
				<div class="mb-12 text-center">
					<h2 class="mb-4 text-4xl font-bold text-gray-900">Technologies We Verify</h2>
					<p class="text-lg text-gray-600">Real production experience with these tools</p>
				</div>

				<div class="grid gap-6 md:grid-cols-3">
					<Card class="border-2 border-purple-200 bg-purple-50">
						<CardContent class="p-6">
							<h3 class="mb-4 text-lg font-bold text-purple-900">Languages & Frameworks</h3>
							<div class="flex flex-wrap gap-2">
								<Badge class="border-purple-300 bg-purple-100 text-purple-700">Python</Badge>
								<Badge class="border-purple-300 bg-purple-100 text-purple-700">SQL</Badge>
								<Badge class="border-purple-300 bg-purple-100 text-purple-700">Scala</Badge>
								<Badge class="border-purple-300 bg-purple-100 text-purple-700">PySpark</Badge>
								<Badge class="border-purple-300 bg-purple-100 text-purple-700">Pandas</Badge>
								<Badge class="border-purple-300 bg-purple-100 text-purple-700">dbt</Badge>
								<Badge class="border-purple-300 bg-purple-100 text-purple-700">Airflow</Badge>
							</div>
						</CardContent>
					</Card>

					<Card class="border-2 border-blue-200 bg-blue-50">
						<CardContent class="p-6">
							<h3 class="mb-4 text-lg font-bold text-blue-900">Data Platforms</h3>
							<div class="flex flex-wrap gap-2">
								<Badge class="border-blue-300 bg-blue-100 text-blue-700">Snowflake</Badge>
								<Badge class="border-blue-300 bg-blue-100 text-blue-700">BigQuery</Badge>
								<Badge class="border-blue-300 bg-blue-100 text-blue-700">Redshift</Badge>
								<Badge class="border-blue-300 bg-blue-100 text-blue-700">Databricks</Badge>
								<Badge class="border-blue-300 bg-blue-100 text-blue-700">Kafka</Badge>
								<Badge class="border-blue-300 bg-blue-100 text-blue-700">Kinesis</Badge>
							</div>
						</CardContent>
					</Card>

					<Card class="border-2 border-teal-200 bg-teal-50">
						<CardContent class="p-6">
							<h3 class="mb-4 text-lg font-bold text-teal-900">Cloud & DevOps</h3>
							<div class="flex flex-wrap gap-2">
								<Badge class="border-teal-300 bg-teal-100 text-teal-700">AWS</Badge>
								<Badge class="border-teal-300 bg-teal-100 text-teal-700">GCP</Badge>
								<Badge class="border-teal-300 bg-teal-100 text-teal-700">Azure</Badge>
								<Badge class="border-teal-300 bg-teal-100 text-teal-700">Docker</Badge>
								<Badge class="border-teal-300 bg-teal-100 text-teal-700">K8s</Badge>
								<Badge class="border-teal-300 bg-teal-100 text-teal-700">Terraform</Badge>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	</section>

	<!-- How It Works - Clean Timeline -->
	<section class="bg-gray-50 py-20">
		<div class="container mx-auto px-4">
			<div class="mx-auto max-w-4xl">
				<div class="mb-16 text-center">
					<h2 class="mb-4 text-4xl font-bold text-gray-900 md:text-5xl">
						From Job Description to Hire in 12 Days
					</h2>
					<p class="text-xl text-gray-600">No fluff. Just results.</p>
				</div>

				<div class="relative">
					<!-- Timeline line -->
					<div class="absolute top-0 bottom-0 left-12 hidden w-1 bg-blue-200 md:block"></div>

					<div class="space-y-12">
						<!-- Step 1 -->
						<div class="relative flex items-start gap-8">
							<div
								class="superellipse z-10 flex h-24 w-24 flex-shrink-0 flex-col items-center justify-center bg-blue-600 text-white shadow-lg"
							>
								<div class="text-3xl font-bold">1</div>
								<div class="text-xs">Day 1</div>
							</div>
							<div class="flex-1 rounded-2xl border-2 border-blue-100 bg-white p-8 shadow-md">
								<h3 class="mb-3 text-2xl font-bold text-gray-900">Drop Your Job Description</h3>
								<p class="mb-4 leading-relaxed text-gray-700">
									No forms, no account creation. Just paste your JD or upload a file. Our AI parses
									what you actually need: "Optimize Snowflake queries and rebuild dbt models" tells
									us way more than "5 years experience required."
								</p>
								<div
									class="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700"
								>
									<svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
										<path
											fill-rule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
											clip-rule="evenodd"
										/>
									</svg>
									<span>Takes 2 minutes</span>
								</div>
							</div>
						</div>

						<!-- Step 2 -->
						<div class="relative flex items-start gap-8">
							<div
								class="superellipse z-10 flex h-24 w-24 flex-shrink-0 flex-col items-center justify-center bg-purple-600 text-white shadow-lg"
							>
								<div class="text-3xl font-bold">2</div>
								<div class="text-xs">Days 1-2</div>
							</div>
							<div class="flex-1 rounded-2xl border-2 border-purple-100 bg-white p-8 shadow-md">
								<h3 class="mb-3 text-2xl font-bold text-gray-900">AI Matches 2,847 Engineers</h3>
								<p class="mb-4 leading-relaxed text-gray-700">
									We scan GitHub repos for evidence of actual work: pipeline optimization commits,
									query performance improvements, data quality frameworks. Engineers who've solved
									problems like yours bubble to the top.
								</p>
								<div class="rounded-lg border border-gray-200 bg-gray-50 p-4">
									<p class="mb-2 text-sm font-medium text-gray-700">We prioritize engineers who:</p>
									<ul class="space-y-1 text-sm text-gray-600">
										<li>✓ Have worked with your exact stack</li>
										<li>✓ Have solved similar technical challenges</li>
										<li>✓ Write maintainable, documented code</li>
										<li>✓ Show production experience, not tutorials</li>
									</ul>
								</div>
							</div>
						</div>

						<!-- Step 3 -->
						<div class="relative flex items-start gap-8">
							<div
								class="superellipse z-10 flex h-24 w-24 flex-shrink-0 flex-col items-center justify-center bg-pink-600 text-white shadow-lg"
							>
								<div class="text-3xl font-bold">3</div>
								<div class="text-xs">Day 2-3</div>
							</div>
							<div class="flex-1 rounded-2xl border-2 border-pink-100 bg-white p-8 shadow-md">
								<h3 class="mb-3 text-2xl font-bold text-gray-900">Review 3-5 Perfect Matches</h3>
								<p class="mb-4 leading-relaxed text-gray-700">
									You get profiles ranked by match quality: GitHub analysis, project highlights,
									specific skills validated, and why they fit. No generic resumes—just engineers
									who've proven they can do the work.
								</p>
								<div
									class="inline-flex items-center gap-2 rounded-lg border border-pink-200 bg-pink-50 px-4 py-2 text-sm text-pink-700"
								>
									<svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
										<path
											fill-rule="evenodd"
											d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
											clip-rule="evenodd"
										/>
									</svg>
									<span>Within 48 hours</span>
								</div>
							</div>
						</div>

						<!-- Step 4 -->
						<div class="relative flex items-start gap-8">
							<div
								class="superellipse z-10 flex h-24 w-24 flex-shrink-0 flex-col items-center justify-center bg-green-600 text-white shadow-lg"
							>
								<div class="text-3xl font-bold">4</div>
								<div class="text-xs">Days 3-12</div>
							</div>
							<div class="flex-1 rounded-2xl border-2 border-green-100 bg-white p-8 shadow-md">
								<h3 class="mb-3 text-2xl font-bold text-gray-900">Interview & Hire</h3>
								<p class="leading-relaxed text-gray-700">
									Schedule first calls within 24 hours. Most companies make an offer within 10-14
									days. You only pay when you hire (15% placement fee for full-time, 10% for
									contractors). If it doesn't work out in 90 days, we find a replacement free.
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Social Proof - Testimonials -->
	<section class="bg-white py-20">
		<div class="container mx-auto px-4">
			<div class="mx-auto max-w-5xl">
				<h2 class="mb-12 text-center text-3xl font-bold text-gray-900">
					What Engineering Leaders Say
				</h2>

				<div class="grid gap-8 md:grid-cols-2">
					<!-- Testimonial 1 -->
					<Card class="border-2 border-blue-200">
						<CardContent class="p-8">
							<div class="mb-6 flex items-center gap-4">
								<div
									class="superellipse flex h-16 w-16 items-center justify-center bg-blue-600 text-2xl font-bold text-white"
								>
									MK
								</div>
								<div>
									<p class="text-lg font-bold text-gray-900">Michael Kim</p>
									<p class="text-sm text-gray-600">VP Engineering, DataCorp</p>
								</div>
							</div>
							<p class="leading-relaxed text-gray-700">
								"Posted on LinkedIn, got 80+ applications. Maybe 5 had actual warehouse experience.
								TalentProfile matched us with someone who'd done the exact Redshift→Snowflake
								migration. Hired in 10 days, migration done in 6 weeks."
							</p>
						</CardContent>
					</Card>

					<!-- Testimonial 2 -->
					<Card class="border-2 border-purple-200">
						<CardContent class="p-8">
							<div class="mb-6 flex items-center gap-4">
								<div
									class="superellipse flex h-16 w-16 items-center justify-center bg-purple-600 text-2xl font-bold text-white"
								>
									SP
								</div>
								<div>
									<p class="text-lg font-bold text-gray-900">Sarah Park</p>
									<p class="text-sm text-gray-600">Director of Data, HealthTech</p>
								</div>
							</div>
							<p class="leading-relaxed text-gray-700">
								"Our Airflow DAGs were a nightmare. TalentProfile found us a contractor who'd built
								similar orchestration systems. She identified 3 bottlenecks in week one. Pipelines
								now run 3x faster."
							</p>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	</section>

	<!-- FAQ - Accordion Style -->
	<section class="bg-white py-20">
		<div class="container mx-auto px-4">
			<div class="mx-auto max-w-3xl">
				<h2 class="mb-12 text-center text-4xl font-bold">Common Questions</h2>

				<div class="space-y-4">
					{#each faqs as faq (faq.question)}
						<div
							class="overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 transition-shadow hover:shadow-md"
						>
							<details class="group">
								<summary class="flex cursor-pointer items-center justify-between p-6">
									<h3 class="pr-4 text-lg font-semibold text-gray-900">{faq.question}</h3>
									<svg
										class="h-5 w-5 flex-shrink-0 text-gray-500 transition-transform group-open:rotate-180"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M19 9l-7 7-7-7"
										/>
									</svg>
								</summary>
								<div class="px-6 pb-6">
									<p class="leading-relaxed text-gray-700">{faq.answer}</p>
								</div>
							</details>
						</div>
					{/each}
				</div>
			</div>
		</div>
	</section>

	<!-- Final CTA - Bold -->
	<section
		class="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 py-24 text-white"
	>
		<!-- Animated background -->
		<div class="absolute inset-0 opacity-20">
			<div
				class="absolute top-0 left-0 z-10 h-96 w-96 animate-pulse rounded-full bg-white blur-3xl"
			></div>
			<div
				class="absolute right-0 bottom-0 z-10 h-96 w-96 animate-pulse rounded-full bg-blue-300 blur-3xl"
				style="animation-delay: 1s"
			></div>
		</div>

		<div class="relative z-10 container mx-auto px-4">
			<div class="mx-auto max-w-4xl text-center">
				<h2 class="mb-6 text-4xl font-bold md:text-6xl">Ready to Hire Real Data Engineers?</h2>
				<p class="mb-10 text-xl leading-relaxed text-white/90 md:text-2xl">
					Drop your job description. Get matched with pre-vetted engineers in 48 hours.
					<span class="font-semibold">First 5 matches free.</span>
				</p>

				<div class="mb-8 flex flex-col justify-center gap-4 sm:flex-row">
					<Button
						size="lg"
						class="transform bg-white px-12 py-8 text-xl font-bold text-indigo-900 shadow-2xl transition-all hover:scale-105 hover:bg-gray-100"
					>
						📄 Submit Job Description
					</Button>
				</div>

				<div class="flex flex-wrap justify-center gap-6 text-sm">
					<div class="flex items-center gap-2">
						<svg class="h-5 w-5 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
							<path
								fill-rule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
								clip-rule="evenodd"
							/>
						</svg>
						<span>No registration</span>
					</div>
					<div class="flex items-center gap-2">
						<svg class="h-5 w-5 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
							<path
								fill-rule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
								clip-rule="evenodd"
							/>
						</svg>
						<span>No upfront cost</span>
					</div>
					<div class="flex items-center gap-2">
						<svg class="h-5 w-5 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
							<path
								fill-rule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
								clip-rule="evenodd"
							/>
						</svg>
						<span>Pay only when you hire</span>
					</div>
				</div>
			</div>
		</div>
	</section>
</div>
